import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Gamepad2, Monitor, Smartphone, HelpCircle, ExternalLink, AlertTriangle, Info } from '../lib/icons';

const SupportPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('playstation');

  const categories = {
    playstation: {
      name: 'PlayStation',
      icon: Gamepad2,
      color: 'bg-[#00BFFF]',
      issues: [
        {
          title: 'PS5 Overheating from Liquid Metal Oxidation',
          description: 'The PS5 uses liquid metal as a thermal interface material between the processor and heatsink. Over time, this liquid metal can oxidize and dry out, leading to poor heat transfer and overheating issues. Common symptoms include the console shutting down during gameplay, loud fan noise, or error messages about temperature.',
          type: 'warning',
          hasVisualGuide: true,
          resources: [
            {
              text: 'Manual PS5 System Software Update',
              url: 'https://www.playstation.com/en-us/support/hardware/ps5/system-software/',
              description: 'Download and manually install the latest PS5 firmware'
            }
          ],
          tips: [
            'Keep your PS5 in a well-ventilated area with at least 4 inches of clearance on all sides',
            'Regularly clean dust from the vents using compressed air',
            'If overheating persists after cleaning, professional liquid metal reapplication may be needed',
            'Add our Cleaning Plan: $75 covers 2 professional cleanings within 2 months when added to any repair'
          ]
        },
        {
          title: 'PS4/PS5 Controller Stick Drift',
          description: 'Stick drift occurs when your controller\'s analog sticks register movement without any input. This is typically caused by worn-out potentiometers inside the analog stick modules. Sony has faced multiple lawsuits over this issue.',
          type: 'warning',
          resources: [
            {
              text: 'Controller Calibration Settings',
              url: 'https://www.playstation.com/en-us/support/hardware/ps5-controller-support/',
              description: 'Try recalibrating your controller before repair'
            }
          ],
          tips: [
            'Try updating your controller firmware first',
            'Test the controller on a different console to confirm the issue',
            'For DIY: If you teardown the controller yourself, spray BW100 directly into potentiometers as a temporary fix',
            <>Professional stick replacement typically takes 2–3 days — or upgrade to <Link to="/custom-controllers" className="text-[#39FF14] hover:text-[#00BFFF] underline underline-offset-2 transition-colors">GuliKit TMR hall-effect joysticks</Link> to permanently eliminate drift</>
          ]
        },
        {
          title: 'PS5 HDMI Port Failure',
          description: 'HDMI port damage is one of the most common PS5 repairs. This often happens from improper cable insertion, pulling the cable at an angle, or the console being moved while connected. Symptoms include no video output, flickering screen, or "No Signal" messages.',
          type: 'info',
          tips: [
            'Always insert and remove HDMI cables straight, never at an angle',
            'Unplug the HDMI cable before moving your console',
            'Use high-quality HDMI cables to prevent connector wear',
            'HDMI port replacement requires microsoldering expertise',
            'We offer magnetic breakaway HDMI adapters for only $20 to prevent future damage',
            'Attempting DIY HDMI repair often causes more damage - see our Failed Repairs gallery'
          ],
          resources: [
            {
              text: 'Watch Our PS5 HDMI Port Replacement',
              url: 'https://youtu.be/xDm8SvSgpI0',
              description: 'Professional PS5 HDMI port repair demonstration'
            },
            {
              text: 'See DIY HDMI Repair Failures',
              url: '/#failed-repairs',
              description: 'Examples of botched DIY repairs that cost more to fix'
            }
          ]
        },
        {
          title: 'Roach/Bug Infested Consoles - How to Treat Before Repair',
          description: 'Cockroaches and other bugs are attracted to the warmth of gaming consoles and can infest them, especially PS4 and PS5 models. If you have a roach-infested console or bought one unknowingly, it\'s important to treat it before bringing it in for repair. Unlike most repair shops, we accept roach-infested consoles, but treating them first makes the repair process safer and faster.',
          type: 'warning',
          tips: [
            'Drench a few paper towels in 91% or higher isopropyl alcohol',
            'Place the alcohol-soaked towels and console in double trash bags',
            'Seal tightly and leave for 24 hours',
            'Most repair shops refuse roach-infested consoles - we accept them after treatment',
            'If you\'re unsure, contact us first - we can guide you through the process',
            'Professional deep cleaning and sanitization available as part of repair service'
          ]
        }
      ]
    },
    xbox: {
      name: 'Xbox',
      icon: Monitor,
      color: 'bg-[#39FF14]',
      issues: [
        {
          title: 'Xbox Series X/S Overheating',
          description: 'The Xbox Series X and S can overheat due to dust accumulation in the internal fan and heatsink. The Series X, in particular, has a large vertical vent that can collect dust over time. Symptoms include the console shutting down during gameplay or displaying overheating warnings.',
          type: 'warning',
          resources: [
            {
              text: 'Xbox System Updates',
              url: 'https://support.xbox.com/en-us/help/hardware-network/settings-updates/update-xbox-wireless-controller',
              description: 'Keep your Xbox console and controller firmware up to date'
            }
          ],
          tips: [
            'Place your Xbox Series X vertically for optimal airflow',
            'Clean the top vent regularly with compressed air',
            'Ensure the console has adequate ventilation space',
            'Professional cleaning includes thermal paste replacement',
            'Add our Cleaning Plan: $75 covers 2 professional cleanings within 2 months when added to any repair'
          ]
        },
        {
          title: 'Xbox Controller Stick Drift',
          description: 'Similar to PlayStation controllers, Xbox controllers also suffer from stick drift. This affects both Xbox One and Series X/S controllers. The issue is caused by wear in the potentiometers of the analog stick modules.',
          type: 'warning',
          resources: [
            {
              text: 'Xbox Controller Support',
              url: 'https://support.xbox.com/en-us/help/hardware-network/controller/controller-analog-stick-solution',
              description: 'Official troubleshooting for stick issues'
            }
          ],
          tips: [
            'Update controller firmware through Xbox accessories app',
            'Try increasing the controller deadzone in game settings',
            'For DIY: If you teardown the controller yourself, spray BW100 directly into potentiometers as a temporary fix',
            <>We offer <Link to="/custom-controllers" className="text-[#39FF14] hover:text-[#00BFFF] underline underline-offset-2 transition-colors">Hall Effect joystick upgrades</Link> for a permanent fix</>

          ]
        },
        {
          title: 'HDMI Port Issues',
          description: 'HDMI port damage affects Xbox Series X/S and older models. The problem typically occurs from improper cable handling or physical impacts to the console while connected.',
          type: 'info',
          tips: [
            'Never force the HDMI cable into the port',
            'Support the cable weight to prevent port stress',
            'Professional HDMI port replacement available for all Xbox models',
            'We offer magnetic breakaway HDMI adapters for only $20 to prevent future damage',
            'DIY HDMI repairs often make things worse - check our Failed Repairs gallery first'
          ],
          resources: [
            {
              text: 'See DIY HDMI Repair Failures',
              url: '/#failed-repairs',
              description: 'Real examples of DIY repairs gone wrong'
            }
          ]
        },
        {
          title: 'Roach/Bug Infested Consoles - How to Treat Before Repair',
          description: 'Gaming consoles provide a warm environment that attracts cockroaches and other insects. Xbox consoles, like PlayStations, can become infested over time. Whether your console has developed an infestation or you purchased one that\'s already infested, proper treatment is essential. We accept roach-infested consoles when most shops turn them away, but treating them beforehand helps ensure a smooth repair process.',
          type: 'warning',
          tips: [
            'Drench a few paper towels in 91% or higher isopropyl alcohol',
            'Place the alcohol-soaked towels and console in double trash bags',
            'Seal tightly and leave for 24 hours',
            'Unlike most repair shops, we don\'t turn away bug-infested consoles',
            'Contact us if you need guidance on the treatment process',
            'We provide professional sanitization and deep cleaning during repair'
          ]
        }
      ]
    },
    nintendo: {
      name: 'Nintendo Switch',
      icon: Smartphone,
      color: 'bg-[#FF1744]',
      issues: [
        {
          title: 'Joy-Con Drift',
          description: 'Joy-Con drift is the most infamous Nintendo Switch issue. The analog sticks register movement without input, making games unplayable. Nintendo has faced class-action lawsuits and now offers free repairs for this issue in many regions. The problem is caused by worn contact pads under the analog stick.',
          type: 'warning',
          resources: [
            {
              text: 'Watch Our Joy-Con Repair Tutorial',
              url: 'https://youtu.be/9kqMtPO4co0',
              description: 'Step-by-step video guide for Joy-Con repairs and maintenance'
            },
            {
              text: 'Nintendo Official Repair Service',
              url: 'https://en-americas-support.nintendo.com/app/ask/ht/789',
              description: 'Nintendo may repair Joy-Con drift for free'
            }
          ],
          tips: [
            'Try recalibrating the sticks in System Settings first',
            'Contact cleaner spray can temporarily fix minor drift',
            <>We offer <Link to="/custom-controllers" className="text-[#39FF14] hover:text-[#00BFFF] underline underline-offset-2 transition-colors">Hall Effect joystick upgrades</Link> for a permanent fix that will never drift</>,
            'Hall Effect sticks use magnetic sensors instead of potentiometers',
            'Professional repair takes 2-3 days'
          ]
        },
        {
          title: 'Charging Port Issues',
          description: 'The USB-C charging port on the Switch can become loose or damaged over time. This prevents the console from charging or docking properly. Common causes include using third-party docks, forcing the cable in the wrong way, or debris in the port.',
          type: 'warning',
          resources: [
            {
              text: 'Watch Our USB-C Port Replacement',
              url: 'https://www.youtube.com/@gamefixsolutions',
              description: 'Professional USB-C port repair demonstration'
            }
          ],
          tips: [
            'Only use compatible Nintendo chargers and docks when possible',
            'Inspect the port for debris before assuming it\'s damaged',
            'Never force the USB-C cable, it should slide in easily',
            'USB-C port replacement requires professional microsoldering'
          ]
        },
        {
          title: 'Won\'t Dock Properly - Check Charger First!',
          description: 'Before assuming the worst, know that a surprisingly large number of "won\'t dock" issues are simply due to a bad charger. The Switch requires at least 15W to dock properly. If your charger is faulty or underpowered, the dock, USB-C port, and P13USB chip could all be working perfectly fine. However, if it is a hardware issue, it\'s often caused by a blown filter near the P13USB chip on the motherboard. This usually happens from power surges, using faulty third-party chargers, or physical damage.',
          type: 'warning',
          resources: [
            {
              text: 'Watch Our Docking Repair Guide',
              url: 'https://www.youtube.com/@gamefixsolutions',
              description: 'Professional repair process for docking issues'
            }
          ],
          tips: [
            'FIRST: Test with a different charger that\'s at least 15W before assuming it\'s a hardware issue',
            'We sell compatible Nintendo chargers if you need a replacement',
            'Symptoms of hardware failure: no video output when docked, slow/no charging with multiple chargers',
            'The blown filter is located near the P13USB chip on the motherboard',
            'This is a microsoldering repair that requires professional equipment',
            'We can diagnose whether it\'s the charger or the console - typically takes 2-3 days',
            'Always use compatible Nintendo chargers (15W minimum) to prevent this issue',
            'Avoid charging with phone chargers or generic USB-C adapters under 15W'
          ]
        }
      ]
    },
    steamdeck: {
      name: 'Steam Deck',
      icon: Gamepad2,
      color: 'bg-[#00BFFF]',
      issues: [
        {
          title: 'SSD Upgrade and SteamOS Installation',
          description: 'Upgrading your Steam Deck\'s SSD is a popular modification to increase storage space. After installing a new SSD, you\'ll need to reinstall SteamOS. The process involves creating a recovery USB drive and booting from it.',
          type: 'info',
          resources: [
            {
              text: 'Download SteamOS Recovery Image',
              url: 'https://help.steampowered.com/en/faqs/view/1B71-EDF2-EB6D-2BB3',
              description: 'Official Valve recovery image for Steam Deck'
            },
            {
              text: 'Steam Deck Recovery Instructions',
              url: 'https://help.steampowered.com/en/faqs/view/1B71-EDF2-EB6D-2BB3',
              description: 'Step-by-step guide from Valve'
            }
          ],
          tips: [
            'We can upgrade your SSD and install SteamOS for you',
            'Use a 2230 M.2 NVMe SSD (not 2242 or 2280)',
            'Keep your original SSD as a backup',
            'The recovery process takes about 30 minutes',
            'Make sure to download the correct image for your Steam Deck model'
          ]
        },
        {
          title: 'Thumbstick Drift',
          description: 'Like other handheld gaming devices, the Steam Deck can develop analog stick drift over time. This is especially common with heavy use in action-packed games.',
          type: 'warning',
          tips: [
            'Try recalibrating the sticks in Steam Deck settings first',
            'Update to the latest SteamOS version',
            'For DIY: If you teardown the device yourself, spray BW100 directly into potentiometers as a temporary fix',
            'Professional stick module replacement available',
            'Valve may cover repairs under warranty'
          ]
        },
        {
          title: 'Fan Noise and Overheating',
          description: 'Some Steam Deck units came with loud or whiny fans. Valve has since updated the fan design. Overheating can also occur from dust buildup or dried thermal paste.',
          type: 'info',
          tips: [
            'Clean the fan vents regularly with compressed air',
            'Limit FPS and TDP in demanding games to reduce heat',
            'Fan replacement and thermal paste reapplication available',
            'Newer fan revisions are quieter and more efficient'
          ]
        }
      ]
    },
    rogally: {
      name: 'ROG Ally',
      icon: Gamepad2,
      color: 'bg-[#FF1744]',
      issues: [
        {
          title: 'SD Card Reader Failure - Blown Resistor',
          description: 'The ASUS ROG Ally has a critical design flaw with the SD card reader. Many units have experienced complete SD card reader failure due to a blown resistor on the motherboard. This issue has affected a significant number of ROG Ally devices and often requires professional microsoldering repair.',
          type: 'warning',
          tips: [
            'Symptoms include SD card not being detected or constant disconnection',
            'Avoid hot-swapping SD cards while the device is running',
            'The issue is caused by a blown fuse resistor near the SD card slot',
            'Repair requires identifying and replacing the blown resistor (typically R3308)',
            'Professional microsoldering required - not a user-serviceable repair',
            'ASUS warranty may cover this issue depending on your region',
            'Consider using a USB-C SD card reader as a temporary workaround'
          ],
          resources: [
            {
              text: 'ASUS ROG Ally Support',
              url: 'https://rog.asus.com/support/',
              description: 'Contact ASUS for warranty repair options'
            }
          ]
        },
        {
          title: 'Thermal Management and Performance',
          description: 'The ROG Ally runs Windows and packs powerful AMD hardware into a small form factor, which can lead to thermal throttling. Proper thermal management is essential for optimal performance.',
          type: 'info',
          tips: [
            'Use performance profiles wisely - Turbo mode generates excessive heat',
            'Keep firmware and Windows updated for thermal optimizations',
            'Clean air vents regularly to maintain airflow',
            'Consider limiting TDP in less demanding games',
            'Professional thermal paste replacement can improve temperatures'
          ]
        },
        {
          title: 'Thumbstick Issues',
          description: 'The ROG Ally uses Hall effect thumbsticks, which are supposed to be drift-resistant. However, some users have reported issues with stick sensitivity or deadzone problems.',
          type: 'info',
          tips: [
            'Update Armoury Crate software for stick calibration options',
            'Adjust deadzones per-game if needed',
            'Hall effect sticks should not develop traditional drift',
            'If issues persist, contact ASUS support for warranty service'
          ]
        }
      ]
    },
    controllers: {
      name: 'Controllers',
      icon: Gamepad2,
      color: 'bg-[#39FF14]',
      issues: [
        {
          title: 'Universal Stick Drift Issue',
          description: 'Stick drift affects controllers from all major manufacturers - PlayStation, Xbox, Nintendo, and third-party brands. It\'s caused by wear in the potentiometers inside the analog stick modules. The issue has led to multiple class-action lawsuits against Sony, Nintendo, and Microsoft.',
          type: 'warning',
          resources: [
            {
              text: 'PlayStation Controller Support',
              url: 'https://www.playstation.com/en-us/support/hardware/ps5-controller-support/',
              description: 'Official PlayStation controller troubleshooting'
            },
            {
              text: 'Xbox Controller Support',
              url: 'https://support.xbox.com/en-us/help/hardware-network/controller/controller-analog-stick-solution',
              description: 'Official Xbox controller troubleshooting'
            }
          ],
          tips: [
            'Update your controller firmware before seeking repair',
            'Try increasing in-game deadzones as a temporary fix',
            'For DIY: If you teardown the controller yourself, spray BW100 directly into potentiometers as a temporary fix',
            'Professional stick module replacement is the permanent solution',
            'Hall effect stick upgrades available for some controller models',
            'Repair typically takes 2-3 days'
          ]
        },
        {
          title: 'Button Responsiveness',
          description: 'Over time, controller buttons can become less responsive or sticky due to debris, wear, or internal contact issues.',
          type: 'info',
          tips: [
            'Try cleaning around buttons with isopropyl alcohol',
            'Compressed air can remove debris from button gaps',
            'For DIY: If you teardown the controller yourself, spray BW100 directly into potentiometers or affected buttons as a temporary fix',
            'Professional cleaning service available',
            'Button membrane replacement may be necessary for worn buttons'
          ]
        },
        {
          title: 'Battery Life Degradation',
          description: 'Rechargeable controller batteries lose capacity over time, especially with frequent charging cycles. This is normal wear and tear but can be frustrating.',
          type: 'info',
          tips: [
            'Avoid leaving controllers on the charger 24/7',
            'Battery replacement available for most wireless controllers',
            'Original batteries typically last 2-4 years with normal use',
            'Professional battery replacement includes warranty'
          ]
        }
      ]
    }
  };

  const currentCategory = categories[selectedCategory as keyof typeof categories];

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return AlertTriangle;
      case 'info':
        return Info;
      default:
        return HelpCircle;
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-[#E0E0E0] mb-4">Support & Resources</h1>
          <p className="text-xl text-[#B0B0B0] max-w-3xl mx-auto">
            Learn about common issues, troubleshooting tips, and helpful resources for your gaming devices
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center mb-12 gap-2">
          {Object.entries(categories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setSelectedCategory(key)}
              className={`flex items-center space-x-2 px-4 py-3 rounded-lg font-semibold transition-all duration-200 ${
                selectedCategory === key
                  ? `${category.color} text-white shadow-lg`
                  : 'bg-gray-800 text-[#E0E0E0] hover:bg-gray-700 border border-gray-700'
              }`}
            >
              <category.icon className="h-5 w-5" />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        {/* Issues List */}
        <div className="space-y-6">
          {currentCategory.issues.map((issue, index) => {
            const IssueIcon = getIssueIcon(issue.type);
            const iconColorClass = issue.type === 'warning' ? 'text-[#FF1744]' : 'text-[#00BFFF]';
            const borderColorClass = issue.type === 'warning' ? 'border-[#FF1744]/30' : 'border-[#00BFFF]/30';

            return (
              <div key={index} className={`bg-gray-900 rounded-2xl p-8 border ${borderColorClass}`}>
                <div className="flex items-start space-x-4 mb-4">
                  <div className={`p-3 rounded-lg ${issue.type === 'warning' ? 'bg-[#FF1744]/20' : 'bg-[#00BFFF]/20'}`}>
                    <IssueIcon className={`h-6 w-6 ${iconColorClass}`} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-[#E0E0E0] mb-3">{issue.title}</h3>
                    <p className="text-[#B0B0B0] text-lg leading-relaxed mb-6">{issue.description}</p>

                    {issue.resources && issue.resources.length > 0 && (
                      <div className="mb-6">
                        <h4 className="text-[#39FF14] font-semibold mb-3 flex items-center">
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Helpful Resources
                        </h4>
                        <div className="space-y-2">
                          {issue.resources.map((resource, idx) => (
                            <a
                              key={idx}
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-start space-x-2 p-3 bg-gray-800/50 rounded-lg hover:bg-gray-800 transition-colors duration-200 group"
                            >
                              <ExternalLink className="h-4 w-4 text-[#00BFFF] mt-0.5 flex-shrink-0" />
                              <div>
                                <div className="text-[#00BFFF] font-medium group-hover:text-[#39FF14] transition-colors">
                                  {resource.text}
                                </div>
                                <div className="text-sm text-[#B0B0B0]">{resource.description}</div>
                              </div>
                            </a>
                          ))}
                        </div>
                      </div>
                    )}

                    {issue.tips && issue.tips.length > 0 && (
                      <div className="bg-gray-800/50 rounded-lg p-4">
                        <h4 className="text-[#39FF14] font-semibold mb-3">Tips & Recommendations</h4>
                        <ul className="space-y-2">
                          {issue.tips.map((tip: React.ReactNode, idx: number) => (
                            <li key={idx} className="flex items-start space-x-2 text-[#E0E0E0]">
                              <span className="text-[#39FF14] mt-1 flex-shrink-0">•</span>
                              <span>{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {issue.hasVisualGuide && (
                      <div className="mt-6">
                        <h4 className="text-[#39FF14] font-semibold mb-4 text-xl">Our 3-Step Liquid Metal Restoration Process</h4>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                          <div className="bg-gray-800/50 rounded-lg p-4 border border-[#00BFFF]/30">
                            <h5 className="text-[#00BFFF] font-semibold mb-3 text-lg">APU (Processor) Restoration</h5>
                            <img
                              src="/apu_4x4.jpg"
                              alt="APU liquid metal oxidation repair process showing before, liquid metal removed, after polishing, and liquid metal reapplied"
                              className="w-full rounded-lg mb-3"
                            />
                            <div className="space-y-2 text-[#B0B0B0] text-sm">
                              <p><span className="text-[#39FF14] font-semibold">Step 1:</span> Remove all existing liquid metal. Notice the severe oxidation visible in the upper right photo - simply spreading the old liquid metal won't fix this.</p>
                              <p><span className="text-[#39FF14] font-semibold">Step 2:</span> Use abrasive compound to polish and remove all oxidation from the APU surface.</p>
                              <p><span className="text-[#39FF14] font-semibold">Step 3:</span> Clean the surface thoroughly (bottom right shows the restored finish), then apply fresh liquid metal.</p>
                            </div>
                          </div>

                          <div className="bg-gray-800/50 rounded-lg p-4 border border-[#00BFFF]/30">
                            <h5 className="text-[#00BFFF] font-semibold mb-3 text-lg">Heat Sink Restoration</h5>
                            <img
                              src="/heat_sink_4x4.jpg"
                              alt="Heat sink liquid metal oxidation repair process showing before, liquid metal removed, after polishing, and liquid metal reapplied"
                              className="w-full rounded-lg mb-3"
                            />
                            <div className="space-y-2 text-[#B0B0B0] text-sm">
                              <p><span className="text-[#39FF14] font-semibold">Same Process:</span> The heat sink undergoes the identical 3-step restoration process.</p>
                              <p><span className="text-[#39FF14] font-semibold">Critical:</span> Both surfaces (APU and heat sink) must be properly polished for optimal heat transfer.</p>
                              <p><span className="text-[#39FF14] font-semibold">Result:</span> Restored thermal performance prevents overheating and extends console life.</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Contact CTA */}
        <div className="mt-16 bg-gradient-to-r from-[#00BFFF]/10 to-[#39FF14]/10 rounded-2xl p-8 border border-[#00BFFF]/30 text-center">
          <h3 className="text-2xl font-bold text-[#E0E0E0] mb-4">Need Professional Help?</h3>
          <p className="text-[#B0B0B0] mb-6 max-w-2xl mx-auto">
            If you're experiencing any of these issues, we're here to help. Our expert technicians can diagnose and repair your device, usually with same-day service.
          </p>
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setTimeout(() => {
                window.location.href = '/#contact';
              }, 500);
            }}
            className="bg-[#FF1744] hover:bg-[#39FF14] text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
          >
            Contact Us for Repair
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupportPage;
